import { useState, useContext, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useInteractionsFiles, useTuples } from '../../../lib/hooks';
import {
    AppContext,
    SttChipUnlocker,
    SttChipUser,
    SttTable,
    SttTopControls,
} from '../../../sporttia/all';
import { copyToClipboard, fullName } from '../../../lib/utils';
import { SttChipAccessType } from '../../../sporttia/chips/SttChipAccessType';
import DateBuilder from '../../../lib/DateBuilder';
import constants from '../../../config/constants';

type Props = {
    id: string | undefined;
};

type Filters = {
    userName: string;
    unlockerKey: string;
    unlocker: string;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    result: string;
};

type UnlockerType =
    | 'PIN'
    | 'QR'
    | 'CARD'
    | 'TICKET_CARD'
    | 'FINGERPRINT'
    | 'TICKET_ONE_USE';

type Unlocker = {
    id: number;
    type?: UnlockerType;
    code?: string;
    user: {
        id: string;
        mship: {
            name?: string;
            surname?: string;
        };
    };
};

type DeviceAccess = {
    id: string;
    created: string;
    direction: string;
    unlocker: Unlocker | null;
    result: string;
    error?: string;
    type: string;
    booking?: {
        id: string | number;
    };
    bonoUser?: {
        id: string | number;
    };
    groupMember?: {
        group?: {
            id: string | number;
        };
    };
    student?: {
        id: string | number;
    };
    boleto?: {
        id: string | number;
    };
};

export default function DeviceAccesses({ id }: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;

    // page usada para la descarga
    const [page, setPage] = useState<number>(1);
    const [accesses, setAccesses] = useState<{
        data: DeviceAccess[];
        total: number;
    } | null>(null);
    const [filters, setFilters] = useTuples<Filters>({
        userName: '',
        unlockerKey: '',
        unlocker: '',
        dateini: format(new Date(), 'yyyy-MM-dd'),
        dateend: format(new Date(), 'yyyy-MM-dd'),
        timeini: '00:00',
        timeend: format(new Date(), 'HH:mm'),
        result: '0',
    });

    const { downloadFile } = useInteractionsFiles();
    const rows = 20;

    const loadAccesses = useCallback(
        (numRowParams = { page: 1, rows }): void => {
            if (!id) return; // Guard against undefined id

            const finalFilters: Partial<Filters> = { ...filters };

            if (finalFilters.result === '0' || finalFilters.result === 'ALL') {
                delete finalFilters.result;
            } else if (finalFilters.result === '1') {
                finalFilters.result = 'SUCCESS';
            } else if (finalFilters.result === '2') {
                finalFilters.result = 'FAILD';
            }

            cxt?.api(
                'GET',
                `${constants.boundedContextPrefixes.MANAGER}/devices/${id}/accesses`,
                {
                    params: {
                        ...numRowParams,
                        ...finalFilters,
                    },
                    success: setAccesses,
                },
            );
        },
        [cxt, filters, id],
    );

    const download = useCallback(
        (fileFormat: 'csv' | 'pdf'): void => {
            const type = fileFormat === 'csv' ? 'text/csv' : 'application/pdf';

            const {
                dateini,
                dateend,
                timeini,
                timeend,
                unlockerKey,
                userName,
                unlocker,
            } = filters;

            let result = '';
            if (filters.result === '1') {
                result = 'SUCCESS';
            } else if (filters.result === '2') {
                result = 'FAILD';
            }

            const formattedDateIni = format(new Date(dateini), 'dd-MM-yyyy');
            const formattedDateEnd = format(new Date(dateend), 'dd-MM-yyyy');
            const filename = `${t('Accesses')}_${formattedDateIni}_${formattedDateEnd}.${fileFormat}`;

            downloadFile(
                `${constants.boundedContextPrefixes.MANAGER}/devices/${id}/accesses.${fileFormat}?page=${page}&rows=${rows}&dateini=${dateini}&dateend=${dateend}&timeini=${timeini}&timeend=${timeend}&unlockerKey=${unlockerKey}&userName=${userName}&unlocker=${unlocker}&result=${result}`,
                type,
                {},
                filename,
            );
        },
        [downloadFile, filters, id, page, t],
    );

    const openItem = useCallback(
        (item: { type: string; id?: string | number; name?: string }): void => {
            if (item.id) {
                copyToClipboard(String(item.id), () =>
                    cxt?.showMessage('S', t('copiedToClipboard')),
                );
            }
        },
        [cxt, t],
    );

    const handleFilterChange = useCallback(
        ({ name, value }: { name: string | undefined; value: unknown }) => {
            if (!name) return;
            setFilters({
                name: name as keyof Filters,
                value: String(value),
            });
        },
        [setFilters],
    );

    const columns = useMemo(
        () => [
            {
                title: '#',
                type: 'tooltip' as const,
                value: (row: DeviceAccess) => ({
                    label: '#',
                    value: row.id,
                }),
                onClick: (row: DeviceAccess) =>
                    copyToClipboard(row.id, () =>
                        cxt?.showMessage('S', t('copiedToClipboard')),
                    ),
            },
            {
                title: t('Date'),
                field: 'created',
                value: (r: DeviceAccess) =>
                    new DateBuilder(
                        r?.created,
                        cxt?.sc?.timezone?.name,
                        cxt?.lang?.key,
                    ).dmyhm(),
            },
            {
                title: t('Direction'),
                value: (row: DeviceAccess) => row.direction,
            },
            {
                title: t('Key'),
                value: (row: DeviceAccess) =>
                    row.unlocker && (
                        <SttChipUnlocker
                            unlocker={{
                                id: row.unlocker.id,
                                type: row.unlocker.type!,
                            }}
                            showModal
                        />
                    ),
            },
            {
                title: t('Method'),
                value: (row: DeviceAccess) => (
                    <SttChipAccessType access={row} onClick={openItem} />
                ),
            },
            {
                title: t('User'),
                value: (row: DeviceAccess) =>
                    row.unlocker &&
                    row.unlocker.user &&
                    row.unlocker.user.mship && (
                        <SttChipUser
                            user={{
                                login: fullName(row.unlocker.user.mship),
                            }}
                        />
                    ),
            },
            {
                title: t('Result'),
                type: 'binaryStatus' as const,
                value: (row: DeviceAccess) => row.result === 'SUCCESS',
            },
            {
                title: t('Error'),
                type: 'text' as const,
                field: 'error',
            },
        ],
        [cxt, openItem, t],
    );

    return (
        <>
            <SttTopControls
                p={2}
                fields={[
                    {
                        xs: 12,
                        sm: 12,
                        md: 5,
                        lg: 4,
                        caption: t('Name'),
                        type: 'text',
                        name: 'userName',
                        value: filters.userName,
                        onEnterKey: () => loadAccesses(),
                    },
                    {
                        xs: 12,
                        sm: 12,
                        md: 5,
                        lg: 4,
                        caption: t('Unlocker'),
                        type: 'text',
                        name: 'unlockerKey',
                        value: filters.unlockerKey,
                        onEnterKey: () => loadAccesses(),
                    },
                ]}
                extraFields={[
                    {
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        type: 'period',
                        caption: '',
                        name: 'period',
                        nameIni: 'dateini',
                        valIni: filters.dateini,
                        nameEnd: 'dateend',
                        valEnd: filters.dateend,
                    },
                    {
                        xs: 6,
                        sm: 4,
                        md: 3,
                        lg: 3,
                        caption: t('TimeIni'),
                        type: 'time',
                        name: 'timeini',
                        value: filters.timeini,
                        interval: 1,
                    },
                    {
                        xs: 6,
                        sm: 4,
                        md: 3,
                        lg: 3,
                        caption: t('TimeEnd'),
                        type: 'time',
                        name: 'timeend',
                        value: filters.timeend,
                        interval: 1,
                    },
                    {
                        xs: 12,
                        sm: 4,
                        md: 6,
                        lg: 6,
                        caption: t('Result'),
                        type: 'select',
                        name: 'result',
                        value: filters.result,
                        mandatory: false,
                        cleanable: false,
                        options: [
                            {
                                label: t('All'),
                                value: 0,
                            },
                            {
                                label: t('Success'),
                                value: 1,
                            },
                            {
                                label: t('Fail'),
                                value: 2,
                            },
                        ] as { label: string; value: number }[],
                    },
                ]}
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: () => download('pdf'),
                    },
                    {
                        caption: `${t('Download')} CSV`,
                        onClick: () => download('csv'),
                    },
                ]}
                onChange={handleFilterChange}
                onFilter={() => loadAccesses()}
            />
            <SttTable
                onFetch={loadAccesses}
                data={accesses}
                onChangePagination={setPage}
                columns={columns}
            />
        </>
    );
}
